import type { FeatureCollection, LineString, Point } from 'geojson';
import type { GeoJSONSource, Map as CarteMapLibre, MapLayerMouseEvent } from 'maplibre-gl';
import { useEffect, useRef, type RefObject } from 'react';

export interface CouchePoints {
  points: FeatureCollection<Point>;
  allees?: FeatureCollection<LineString>;
  onPoint: (id: string) => void;
  onEmprise?: (bbox: [number, number, number, number]) => void;
}

const VIDE: FeatureCollection = { type: 'FeatureCollection', features: [] };
const SOURCE = 'places-regroupees';

/** Couche GeoJSON indépendante des marqueurs HTML, restaurée après chaque changement de thème. */
export function usePointsRegroupes(
  carte: RefObject<CarteMapLibre | null>,
  pret: boolean,
  couche?: CouchePoints,
) {
  const courant = useRef(couche);
  courant.current = couche;
  const presente = Boolean(couche);
  useEffect(() => {
    const c = carte.current;
    if (!c || !pret || !presente) return;
    let actif = true;
    const couleur = (nom: string, repli: string) =>
      getComputedStyle(document.documentElement).getPropertyValue(nom).trim() || repli;
    const installer = () => {
      if (!actif || !courant.current) return;
      if (!c.getSource(SOURCE))
        c.addSource(SOURCE, {
          type: 'geojson',
          data: courant.current.points,
          cluster: true,
          clusterMaxZoom: 17,
          clusterRadius: 45,
        });
      if (!c.getSource('allees-marche'))
        c.addSource('allees-marche', { type: 'geojson', data: courant.current.allees ?? VIDE });
      if (!c.getLayer('allees-marche'))
        c.addLayer({
          id: 'allees-marche',
          type: 'line',
          source: 'allees-marche',
          paint: {
            'line-color': couleur('--cc-encre-3', '#5c626b'),
            'line-width': 2,
            'line-dasharray': [3, 2],
          },
        });
      if (!c.getLayer('groupes-places'))
        c.addLayer({
          id: 'groupes-places',
          type: 'circle',
          source: SOURCE,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': couleur('--cc-accent', '#e3a42d'),
            'circle-radius': ['step', ['get', 'point_count'], 16, 100, 21, 1000, 27],
            'circle-stroke-color': couleur('--cc-encre', '#20242b'),
            'circle-stroke-width': 1,
          },
        });
      if (!c.getLayer('places-unitaires'))
        c.addLayer({
          id: 'places-unitaires',
          type: 'circle',
          source: SOURCE,
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-radius': 6,
            'circle-stroke-width': 2,
            'circle-stroke-color': [
              'match',
              ['get', 'etat'],
              'attribue',
              couleur('--cc-vert', '#46745b'),
              'reserve',
              couleur('--cc-bleu', '#3f6d9d'),
              couleur('--cc-ligne-2', '#d3cfc4'),
            ],
            'circle-color': [
              'match',
              ['get', 'etat'],
              'attribue',
              couleur('--cc-vert-clair', '#e9e6de'),
              'reserve',
              couleur('--cc-bleu-clair', '#e9e6de'),
              'hors_service',
              couleur('--cc-surface-2', '#e9e6de'),
              couleur('--cc-surface', '#ffffff'),
            ],
          },
        });
      if (c.getStyle().glyphs && !c.getLayer('nombre-places'))
        c.addLayer({
          id: 'nombre-places',
          type: 'symbol',
          source: SOURCE,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 12,
          },
          paint: { 'text-color': couleur('--cc-encre', '#20242b') },
        });
    };
    const ouvrir = (e: MapLayerMouseEvent) => {
      const id = e.features?.[0]?.properties?.id;
      if (typeof id === 'string') courant.current?.onPoint(id);
    };
    const zoomer = (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      const source = c.getSource(SOURCE) as GeoJSONSource | undefined;
      if (!source || f?.geometry.type !== 'Point') return;
      const centre = f.geometry.coordinates as [number, number];
      void source
        .getClusterExpansionZoom(Number(f.properties?.cluster_id))
        .then((zoom) => {
          if (actif)
            c.easeTo({
              center: centre,
              zoom,
              duration: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
            });
        })
        .catch(() => undefined);
    };
    const emprise = () => {
      const b = c.getBounds();
      courant.current?.onEmprise?.([
        Math.max(-180, b.getWest()),
        Math.max(-90, b.getSouth()),
        Math.min(180, b.getEast()),
        Math.min(90, b.getNorth()),
      ]);
    };
    c.on('style.load', installer);
    c.on('click', 'places-unitaires', ouvrir);
    c.on('click', 'groupes-places', zoomer);
    c.on('moveend', emprise);
    installer();
    emprise();
    return () => {
      actif = false;
      c.off('style.load', installer);
      c.off('click', 'places-unitaires', ouvrir);
      c.off('click', 'groupes-places', zoomer);
      c.off('moveend', emprise);
    };
  }, [carte, pret, presente]);

  useEffect(() => {
    const c = carte.current;
    if (!c || !pret) return;
    (c.getSource(SOURCE) as GeoJSONSource | undefined)?.setData(couche?.points ?? VIDE);
    (c.getSource('allees-marche') as GeoJSONSource | undefined)?.setData(couche?.allees ?? VIDE);
  }, [carte, pret, couche?.points, couche?.allees]);
}
